<template>

  <PageShell :page="page" :show-mobile-download="showMobileDownload">

    <div class="article-content">

      <section v-if="pageVisual" class="article-visual" :class="'article-visual--' + page" aria-label="Page visual summary">

        <div class="article-visual-main">

          <div class="article-visual-copy">

            <span class="article-visual-kicker">{{ pageVisual.kicker }}</span>

            <div class="article-visual-title">{{ pageVisual.title }}</div>

            <p>{{ pageVisual.summary }}</p>

            <div class="article-visual-tags">

              <span v-for="tag in pageVisual.tags" :key="tag">{{ tag }}</span>

            </div>

          </div>

          <div class="article-visual-media">

            <div v-if="pageVisual.gallery" class="article-phone-gallery">

              <img v-for="image in pageVisual.gallery" :key="image.src" :src="image.src" :alt="image.alt" />

            </div>

            <img v-else class="article-visual-image" :src="pageVisual.image" :alt="pageVisual.imageAlt" />

            <img v-if="pageVisual.secondaryImage" class="article-visual-thumb" :src="pageVisual.secondaryImage" :alt="pageVisual.secondaryAlt" />

          </div>

        </div>

        <div class="article-visual-cards">

          <div v-for="card in pageVisual.cards" :key="card.title" class="article-visual-card">

            <span>{{ card.step }}</span>

            <strong>{{ card.title }}</strong>

            <small>{{ card.text }}</small>

          </div>

        </div>

      </section>

      <article v-html="contentHtml" @click="handleContentClick" />

      <FAQ v-if="faqs && faqs.length" :faqs="faqs" />

    </div>

  </PageShell>

</template>



<script>

import PageShell from '@/components/PageShell.vue';

import FAQ from '@/components/FAQ.vue';

import apkDownload from '@/mixins/apkDownload';

import { brandAssetMap } from '@/config/brandAssets';

import aeroShot1 from '@/assets/aero/aero-shot-1.webp';

import aeroShot2 from '@/assets/aero/aero-shot-2.webp';

import aeroShot3 from '@/assets/aero/aero-shot-3.webp';

import aeroShot4 from '@/assets/aero/aero-shot-4.webp';

import aeroShot5 from '@/assets/aero/aero-shot-5.webp';

import installSteps from '@/assets/inatall-gbwhatsapp-steps.webp';

import banAccount from '@/assets/ban-account.webp';

import temporaryBan from '@/assets/GB-WhatsApp-temporary-ban-timer.webp';



const assetMap = {

  ...brandAssetMap,

  '@/assets/aero/aero-shot-1.webp': aeroShot1,

  '@/assets/aero/aero-shot-2.webp': aeroShot2,

  '@/assets/aero/aero-shot-3.webp': aeroShot3,

  '@/assets/aero/aero-shot-4.webp': aeroShot4,

  '@/assets/aero/aero-shot-5.webp': aeroShot5,

};



export default {

  name: 'ArticlePage',

  components: { PageShell, FAQ },

  mixins: [apkDownload],

  props: {

    page: { type: String, required: true },

    content: { type: String, required: true },

    faqs: { type: Array, default: null },

    showMobileDownload: { type: Boolean, default: false },

  },

  computed: {

    pageVisual() {

      const visuals = {

        features: {
          kicker: 'Feature Overview',
          title: 'Privacy, themes, and media tools in one view',
          summary: 'A quick visual snapshot of the main WhatsApp Aero areas before the full feature breakdown.',
          tags: ['Privacy', 'Themes', 'Media', 'Security'],
          gallery: [
            { src: aeroShot1, alt: 'WhatsApp Aero chat interface preview' },
            { src: aeroShot2, alt: 'WhatsApp Aero theme preview' },
            { src: aeroShot4, alt: 'WhatsApp Aero privacy controls preview' },
          ],
          cards: [
            { step: '01', title: 'Privacy Control', text: 'Hide status, ticks, typing, and activity signals.' },
            { step: '02', title: 'Custom Look', text: 'Themes, colors, fonts, icons, and chat layouts.' },
            { step: '03', title: 'Media Sharing', text: 'More flexible sharing for images, videos, and files.' },
          ],
        },

        install: {
          kicker: 'Install Guide',
          title: 'Follow the APK setup path step by step',
          summary: 'Use the visual flow to understand preparation, Android permission, installation, and account setup.',
          tags: ['Backup', 'Unknown Apps', 'APK Install', 'Verify'],
          image: installSteps,
          imageAlt: 'Android APK installation permission steps',
          cards: [
            { step: '01', title: 'Prepare', text: 'Back up chats and confirm storage before installing.' },
            { step: '02', title: 'Allow', text: 'Enable Android installation permission for the APK source.' },
            { step: '03', title: 'Launch', text: 'Open the app, verify the number, and restore chats if needed.' },
          ],
        },

        update: {
          kicker: 'Update Guide',
          title: 'Refresh the app without losing your setup',
          summary: 'A cleaner update flow helps keep chats, settings, and the latest WhatsApp Aero build together.',
          tags: ['Latest Version', 'Backup First', 'Install Over', 'Check Stability'],
          gallery: [
            { src: aeroShot2, alt: 'WhatsApp Aero theme preview' },
            { src: aeroShot3, alt: 'WhatsApp Aero update interface preview' },
            { src: aeroShot5, alt: 'WhatsApp Aero settings preview' },
          ],
          cards: [
            { step: '01', title: 'Back Up', text: 'Save messages and media before replacing the old build.' },
            { step: '02', title: 'Install', text: 'Use the latest APK and install over the current version.' },
            { step: '03', title: 'Review', text: 'Open the app and confirm chats, login, and features work.' },
          ],
        },

        antiban: {
          kicker: 'Safety Guide',
          title: 'Keep account risk visible before daily use',
          summary: 'The safety section is easier to scan with reminders for updates, source checks, and cautious usage.',
          tags: ['Anti-Ban', 'Trusted Source', 'Stable Version', 'Backup'],
          image: banAccount,
          imageAlt: 'WhatsApp account ban warning example',
          secondaryImage: temporaryBan,
          secondaryAlt: 'Temporary ban timer warning example',
          cards: [
            { step: '01', title: 'Stay Updated', text: 'Older builds are more likely to trigger stability issues.' },
            { step: '02', title: 'Avoid Spam', text: 'Mass messaging and automation increase account risk.' },
            { step: '03', title: 'Watch Signals', text: 'Restrictions, logouts, and failed delivery need attention.' },
          ],
        },

      };

      return visuals[this.page] || null;

    },

    contentHtml() {

      let html = this.content;

      Object.keys(assetMap).forEach((key) => {

        html = html.replace(new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), assetMap[key]);

      });

      html = this.enhanceStepHeadings(html);

      return html;

    },

  },

  methods: {

    handleContentClick(e) {

      if (e.target.closest && e.target.closest('.download-trigger')) {

        this.downloadApk();

      }

    },

    enhanceStepHeadings(html) {
      return html.replace(/<(h3|p)>(Step\s+(\d+))(?::\s*)?([^<]*)<\/\1>/gi, (match, tag, stepLabel, stepNumber, stepText) => {
        const text = stepText ? `<span class="article-step-text">${stepText}</span>` : '';
        return `<${tag} class="article-step-heading article-step-heading--${tag}"><span class="article-step-badge">${stepLabel}</span>${text}</${tag}>`;
      });
    },

  },

};

</script>



<style lang="scss" scoped>

.article-visual {

  margin: 0 0 34px;

  padding: 22px;

  border: 1px solid #dfe7f0;

  border-radius: 8px;

  background:
    radial-gradient(circle at 92% 12%, rgba(140, 195, 122, 0.16), transparent 30%),
    linear-gradient(135deg, #f7fafc 0%, #ffffff 58%, #f4f9f1 100%);

  box-shadow: 0 16px 34px rgba(53, 84, 143, 0.08);

}

.article-visual-main {

  display: grid;

  grid-template-columns: minmax(0, 1fr) minmax(300px, 42%);

  align-items: center;

  gap: 28px;

}

.article-visual-copy {

  min-width: 0;

}

.article-visual-kicker {

  display: inline-flex;

  align-items: center;

  min-height: 26px;

  padding: 3px 10px;

  border: 1px solid rgba(53, 84, 143, 0.16);

  border-radius: 999px;

  background: #fff;

  color: #35548f;

  font-size: 13px;

  font-weight: 700;

  line-height: 1.2;

}

.article-visual-title {

  max-width: 620px;

  margin: 14px 0 10px;

  color: #233d6f;

  font-size: 28px;

  font-weight: 800;

  line-height: 1.18;

}

.article-visual-copy p {

  max-width: 620px;

  margin: 0 0 16px;

  color: #536176;

  font-size: 16px;

  line-height: 1.65;

}

.article-visual-tags {

  display: flex;

  flex-wrap: wrap;

  gap: 8px;

  span {

    display: inline-flex;

    align-items: center;

    min-height: 30px;

    padding: 4px 10px;

    border: 1px solid #d8e4ef;

    border-radius: 6px;

    background: #fff;

    color: #526070;

    font-size: 13px;

    font-weight: 700;

    line-height: 1.2;

  }

}

.article-visual-media {

  position: relative;

  min-height: 220px;

  display: flex;

  align-items: center;

  justify-content: center;

}

.article-visual-image {

  width: 100%;

  max-height: 280px;

  object-fit: contain;

  border: 1px solid #d8e4ef;

  border-radius: 8px;

  background: #fff;

  box-shadow: 0 18px 34px rgba(53, 84, 143, 0.13);

}

.article-visual-thumb {

  position: absolute;

  right: -8px;

  bottom: -12px;

  width: 46%;

  max-width: 180px;

  border: 1px solid #d8e4ef;

  border-radius: 8px;

  background: #fff;

  box-shadow: 0 14px 28px rgba(53, 84, 143, 0.2);

}

.article-phone-gallery {

  width: 100%;

  min-height: 260px;

  display: flex;

  align-items: center;

  justify-content: center;

  gap: 0;

  img {

    width: 31%;

    min-width: 88px;

    max-width: 140px;

    aspect-ratio: 9 / 19;

    object-fit: cover;

    border: 1px solid #d8e4ef;

    border-radius: 8px;

    background: #fff;

    box-shadow: 0 18px 30px rgba(53, 84, 143, 0.16);

    &:nth-child(1) {

      transform: translateX(18px) rotate(-5deg);

    }

    &:nth-child(2) {

      position: relative;

      z-index: 2;

      width: 34%;

      max-width: 154px;

    }

    &:nth-child(3) {

      transform: translateX(-18px) rotate(5deg);

    }

  }

}

.article-visual-cards {

  display: grid;

  grid-template-columns: repeat(3, minmax(0, 1fr));

  gap: 12px;

  margin-top: 20px;

}

.article-visual-card {

  min-height: 118px;

  padding: 14px;

  border: 1px solid #dfe7f0;

  border-radius: 8px;

  background: rgba(255, 255, 255, 0.88);

  box-shadow: 0 10px 22px rgba(53, 84, 143, 0.06);

  span {

    display: inline-flex;

    align-items: center;

    justify-content: center;

    width: 30px;

    height: 30px;

    margin-bottom: 10px;

    border-radius: 50%;

    background: #35548f;

    color: #fff;

    font-size: 12px;

    font-weight: 800;

  }

  strong {

    display: block;

    margin-bottom: 6px;

    color: #233d6f;

    font-size: 16px;

    line-height: 1.25;

  }

  small {

    display: block;

    color: #5b6675;

    font-size: 13px;

    line-height: 1.45;

  }

}

.article-visual--install .article-visual-image,
.article-visual--antiban .article-visual-image {

  padding: 10px;

}

::v-deep .faq {

  max-width: 1080px;

  margin: 0 auto 48px;

  padding: 0 16px;



  h3 {

    color: #35548f;

  }

}

@media (max-width: 768px) {

  .article-visual {

    margin-bottom: 26px;

    padding: 16px;

  }

  .article-visual-main {

    grid-template-columns: 1fr;

    gap: 18px;

  }

  .article-visual-title {

    font-size: 22px;

  }

  .article-visual-media {

    min-height: 190px;

  }

  .article-phone-gallery {

    min-height: 220px;

    img {

      width: 33%;

      min-width: 78px;

    }

  }

  .article-visual-cards {

    grid-template-columns: 1fr;

  }

  .article-visual-card {

    min-height: 0;

  }

  .article-visual-thumb {

    right: 4px;

    bottom: -10px;

    width: 42%;

  }

}

</style>

